import keyBy from 'lodash/keyBy';
import get from 'lodash/get';
import { useOrganizationStore } from '@/stores/organization.store';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import { $canOrg } from '@pendo/permissions';
import {
    fetchSubEssentialContacts,
    fetchOrgEssentialContacts,
    createOrUpdateSubEssentialContact,
    createOrUpdateOrgEssentialContact,
} from './essential-contacts.utils';

export default {
    namespaced: true,
    state: {
        error: null,
        fetching: false,
        map: {},
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { key, contact }) {
            state.map[key] = contact;
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
    },
    actions: {
        async loadAll ({ state, commit }, { reFetch = false } = {}) {
            if (!isEmpty(state.map) && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });

            try {
                const subContacts = await fetchSubEssentialContacts();
                subContacts.forEach((contact) => {
                    contact.type = 'sub';
                });

                let orgContacts = [];
                const hasOrgViewAccess = $canOrg()
                    .entity('organizationSetting')
                    .action('view');
                if (hasOrgViewAccess) {
                    const activeOrganization = useOrganizationStore().getActive;
                    const orgId = get(activeOrganization, 'id', null);
                    orgContacts = await fetchOrgEssentialContacts(orgId);
                    orgContacts.forEach((contact) => {
                        contact.type = 'org';
                    });
                }
                commit('setMap', { map: keyBy([...subContacts, ...orgContacts], ({ type, id }) => `${type}_${id}`) });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async createOrUpdateSub ({ commit }, contact) {
            commit('setFetching', { fetching: true });

            try {
                const updatedContact = await createOrUpdateSubEssentialContact(contact);
                const key = `sub_${updatedContact.id}`;
                commit('setMapAtKey', {
                    key,
                    contact: Object.freeze({ ...updatedContact, type: 'sub' }),
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async createOrUpdateOrg ({ commit }, contact) {
            commit('setFetching', { fetching: true });
            try {
                const activeOrganization = useOrganizationStore().getActive;
                const orgId = get(activeOrganization, 'id', null);
                const updatedContact = await createOrUpdateOrgEssentialContact(orgId, contact);
                const key = `org_${updatedContact.id}`;
                commit('setMapAtKey', {
                    key,
                    contact: Object.freeze({ ...updatedContact, type: 'org' }),
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
    },
    getters: {
        list: (state) => values(state.map),
        error: (state) => state.error,
        fetching: (state) => state.fetching,
    },
};
